import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Button } from "@repo/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@repo/ui/avatar";
import { Badge } from "@repo/ui/badge";
import { LogOut, Shield, Bell } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth, type AppRole } from "@/hooks/useAuth";
import {
  AuditNotificationRow,
  type NotificationActionType,
  type NotificationRowData,
} from "@/components/notifications/AuditNotificationRow";
import { useJobHealthMonitor } from "@/hooks/useJobHealthMonitor";
import { fetchAuditNotifications, pauseJobRun, cancelJobRun } from "@/lib/api";
import type { CrawlLogEntry } from "@/lib/api";

const roleConfig: Record<NonNullable<AppRole>, { label: string; className: string }> = {
  admin: { label: "Admin", className: "bg-primary/15 text-primary" },
  operator: { label: "Operator", className: "bg-accent/15 text-accent" },
  viewer: { label: "Viewer", className: "bg-muted text-muted-foreground" },
};
const DISMISSED_NOTIFICATIONS_STORAGE_KEY = "header-dismissed-notifications";
const HEADER_MAX_VISIBLE_NOTIFICATIONS = 10;
const HEADER_FETCH_LIMIT = 50;

export const Header = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, role } = useAuth();
  const [notifications, setNotifications] = useState<CrawlLogEntry[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => {
    if (typeof window === "undefined") return new Set();
    try {
      const raw = window.sessionStorage.getItem(DISMISSED_NOTIFICATIONS_STORAGE_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw) as string[];
      return new Set(parsed);
    } catch (_err) {
      return new Set();
    }
  });
  const [showNotifications, setShowNotifications] = useState(false);
  const { alerts: healthAlerts, dismissAlert: dismissHealthAlert, clearAlerts: clearHealthAlerts } = useJobHealthMonitor();

  const loadNotifications = useCallback(async () => {
    try {
      const result = await fetchAuditNotifications({ limit: HEADER_FETCH_LIMIT, offset: 0 });
      setNotifications(result.data);
    } catch (error) {
      console.error("Failed to load header notifications:", error);
    }
  }, []);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        DISMISSED_NOTIFICATIONS_STORAGE_KEY,
        JSON.stringify(Array.from(dismissedIds)),
      );
    } catch (_err) {
      // Ignore storage errors so notifications still work.
    }
  }, [dismissedIds]);

  // Listen for crawl_logs warnings/errors on running jobs
  useEffect(() => {
    loadNotifications();

    const channel = supabase
      .channel('header_notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'crawl_logs',
          filter: 'level=in.(warn,error)',
        },
        () => {
          loadNotifications();
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [loadNotifications]);

  const healthNotificationRows: NotificationRowData[] = healthAlerts.map((alert) => ({
    id: `health-${alert.id}`,
    job_id: alert.jobId,
    job_run_id: alert.runId,
    level: alert.type === "error" ? "error" : alert.type === "warning" ? "warn" : "info",
    message: alert.message,
    metadata: { category: alert.category, source: "health-monitor" },
    created_at: alert.timestamp.toISOString(),
    scrape_jobs: { id: alert.jobId, name: alert.jobName },
    actions: alert.actions?.map((action) => ({ label: action.label, action: action.action })),
  }));

  const databaseNotificationRows: NotificationRowData[] = notifications;
  const mergedNotifications = [...healthNotificationRows, ...databaseNotificationRows].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
  const undisposedNotifications = mergedNotifications.filter((n) => !dismissedIds.has(n.id));
  const visibleNotifications = undisposedNotifications.slice(0, HEADER_MAX_VISIBLE_NOTIFICATIONS);
  const totalNotificationCount = visibleNotifications.length;

  const dismissNotification = (id: string) => {
    if (id.startsWith("health-")) {
      dismissHealthAlert(id.slice("health-".length));
    }
    setDismissedIds((prev) => new Set(prev).add(id));
  };

  const handleNotificationAction = async (action: NotificationActionType, runId: string) => {
    if (action === "view") {
      navigate("/dashboard/jobs");
      setShowNotifications(false);
      return;
    }

    if (action === "pause") {
      const result = await pauseJobRun(runId);
      toast({
        title: result.success ? "Paused" : "Error",
        description: result.success ? "Job has been paused" : result.error,
        variant: result.success ? "default" : "destructive",
      });
      if (result.success) setTimeout(loadNotifications, 1000);
      return;
    }

    if (action === "cancel") {
      const result = await cancelJobRun(runId);
      toast({
        title: result.success ? "Cancelled" : "Error",
        description: result.success ? "Job has been cancelled" : result.error,
        variant: result.success ? "default" : "destructive",
      });
      if (result.success) setTimeout(loadNotifications, 1000);
    }
  };

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    } else {
      navigate("/auth");
    }
  };

  const getInitials = (email: string) => {
    return email.slice(0, 2).toUpperCase();
  };

  if (!user) return null;

  return (
    <header className="h-16 border-b border-border bg-card px-6 flex items-center justify-between sticky top-0 z-30">
      <div>
        {/* Breadcrumb or page title can go here */}
      </div>

      <div className="flex items-center gap-3">
        {/* Notification Bell */}
        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <Bell className="h-5 w-5" />
            {totalNotificationCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground text-[10px] flex items-center justify-center">
                {totalNotificationCount}
              </span>
            )}
          </Button>

          {/* Notification Dropdown */}
          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-96 bg-card border border-border rounded-lg shadow-lg z-50">
              <div className="p-3 border-b border-border flex items-center justify-between">
                <span className="text-sm font-medium">Notifications</span>
                {totalNotificationCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-6"
                    onClick={() => {
                      setDismissedIds((prev) => {
                        const next = new Set(prev);
                        for (const notification of visibleNotifications) {
                          next.add(notification.id);
                        }
                        return next;
                      });
                      clearHealthAlerts();
                    }}
                  >
                    Clear all
                  </Button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {totalNotificationCount === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No notifications
                  </div>
                ) : (
                  visibleNotifications.map((notification) => (
                    <div key={notification.id} className="p-2 border-b border-border last:border-0">
                      <AuditNotificationRow
                        notification={notification}
                        variant="dropdown"
                        onDismiss={dismissNotification}
                        onAction={handleNotificationAction}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2 px-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                  {getInitials(user.email || "U")}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:flex flex-col items-start">
                <span className="text-sm font-medium max-w-32 truncate">
                  {user.email}
                </span>
                {role && (
                  <Badge className={`${roleConfig[role].className} text-xs h-5`}>
                    {roleConfig[role].label}
                  </Badge>
                )}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="font-medium">Account</span>
                <span className="text-xs text-muted-foreground truncate">
                  {user.email}
                </span>
                {role && (
                  <div className="flex items-center gap-1 mt-1">
                    <Shield className="w-3 h-3" />
                    <span className="text-xs capitalize">{role}</span>
                  </div>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive">
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};